import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/session";
import { formatEventDate } from "@/lib/dates";
import { Ticket, ChevronRight, Star } from "lucide-react";

const SPEED_PX_PER_SEC = 28;
const MAX_DELTA_MS = 50; // cap large elapsed deltas (e.g. after a background tab)
const TOUCH_RESUME_MS = 1200;

// Pure, testable animation step. Advances scrollLeft by elapsed time and wraps
// seamlessly at the width of one item group (the track renders the group twice).
export function tickerStep({ scrollLeft, elapsedMs, speed = SPEED_PX_PER_SEC, loopWidth, maxDeltaMs = MAX_DELTA_MS }) {
  if (!loopWidth || loopWidth <= 0) return scrollLeft;
  const dt = Math.min(Math.max(elapsedMs, 0), maxDeltaMs);
  let next = scrollLeft + (speed * dt) / 1000;
  if (next >= loopWidth) next -= loopWidth;
  return next;
}

// Slim, automated GameDay Travel Ticker. Surfaces upcoming sports-travel opportunities
// from the real published event database and links only to internal Hotelstrips event pages.
export function TravelTicker() {
  const { pathname } = useLocation();
  const [items, setItems] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);

  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const offsetRef = useRef(0);
  const touchTimeoutRef = useRef(null);

  useEffect(() => {
    api.get("/ticker").then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Continuous right-to-left auto-scroll via requestAnimationFrame (no setInterval).
  useEffect(() => {
    if (reducedMotion || !items.length) return;
    const el = scrollRef.current;
    if (!el) return;

    let rafId;
    let last = null;
    offsetRef.current = el.scrollLeft;
    // Accumulate the position as a float; reading the DOM's rounded scrollLeft each
    // frame would discard the sub-pixel advance and stall the ticker.
    const frame = (now) => {
      if (last == null) last = now;
      const elapsedMs = now - last;
      last = now;
      if (!pausedRef.current) {
        offsetRef.current = tickerStep({
          scrollLeft: offsetRef.current,
          elapsedMs,
          loopWidth: el.scrollWidth / 2,
        });
        el.scrollLeft = offsetRef.current;
      } else {
        // While paused, keep the accumulator in sync with any manual/touch scroll
        // so motion resumes from the user's position without a jump.
        offsetRef.current = el.scrollLeft;
      }
      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafId);
  }, [reducedMotion, items.length]);

  useEffect(() => () => clearTimeout(touchTimeoutRef.current), []);

  if (!items.length) return null;

  const fmt = (it) => formatEventDate(it.date, it.timezone, { month: "short", day: "numeric" });
  const track = (it) =>
    api.post("/ticker/click", {
      event_slug: it.slug, venue_slug: it.venue_slug, page: pathname,
      position: "ticker", session_id: getSessionId(),
    }).catch(() => {});

  const pause = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };
  const onTouchStart = () => {
    clearTimeout(touchTimeoutRef.current);
    pausedRef.current = true;
  };
  const onTouchEnd = () => {
    clearTimeout(touchTimeoutRef.current);
    touchTimeoutRef.current = setTimeout(() => { pausedRef.current = false; }, TOUCH_RESUME_MS);
  };

  const renderItem = (it, groupHidden) => (
    <Link
      key={`${groupHidden ? "dup" : "live"}-${it.slug}`}
      to={`/events/${it.slug}`}
      onClick={groupHidden ? undefined : () => track(it)}
      data-testid={groupHidden ? undefined : `ticker-item-${it.slug}`}
      tabIndex={groupHidden ? -1 : undefined}
      className="group inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 px-3 py-1 text-xs transition-colors"
    >
      {it.featured && <Star className="h-3 w-3 shrink-0 fill-primary text-primary" />}
      <span className="font-semibold text-white/90">{it.title}</span>
      <span className="text-white/50">· {it.venue_name} · {fmt(it)}</span>
      <span className="inline-flex items-center gap-0.5 font-heading font-bold text-primary">
        {it.cta}<ChevronRight className="h-3 w-3" />
      </span>
    </Link>
  );

  return (
    <div data-testid="travel-ticker" className="border-b border-white/10 bg-ink text-white">
      <div className="mx-auto max-w-7xl flex items-center gap-3 px-3 sm:px-6 h-10">
        <span className="hidden sm:flex items-center gap-1.5 shrink-0 overline text-primary">
          <Ticket className="h-3.5 w-3.5" /> GameDay
        </span>
        <div
          ref={scrollRef}
          data-testid="ticker-scroll"
          className="flex-1 overflow-x-auto no-scrollbar"
          onMouseEnter={pause}
          onMouseLeave={resume}
          onFocus={pause}
          onBlur={resume}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <div className="flex items-center gap-2 whitespace-nowrap w-max">
            <div className="flex items-center gap-2">
              {items.map((it) => renderItem(it, false))}
            </div>
            {!reducedMotion && (
              <div className="flex items-center gap-2 pl-2" aria-hidden="true">
                {items.map((it) => renderItem(it, true))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
